import React, { useMemo } from 'react';
import { Box, Typography } from '@compass/core';
import {
    Chart,
    Series,
    ArgumentAxis,
    ValueAxis,
    Legend,
    Tooltip,
    Label,
    Title,
    Font,
    Export,
} from 'devextreme-react/chart';
import { SchoolEnrollmentSummaryDto } from 'generated/openApi';

interface SchoolEnrollmentChartProps {
    data: SchoolEnrollmentSummaryDto[];
    chartRef: React.RefObject<Chart>;
    filter: { schoolYear: number | null };
}

export const SchoolEnrollmentChart = ({ data, chartRef, filter }: SchoolEnrollmentChartProps) => {
    // Prepare metadata for export
    const exportMetadata = useMemo(() => {
        const yearLabel = filter.schoolYear 
            ? `${filter.schoolYear}-${filter.schoolYear + 1}` 
            : 'Current Year';
        const exportDate = new Date().toLocaleDateString();
        
        return {
            title: 'Schools with Highest Enrollments',
            subtitle: `Year: ${yearLabel} | Exported: ${exportDate}`
        };
    }, [filter.schoolYear]);

    if (data.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 400,
                    color: 'text.secondary',
                }}
            >
                <Typography variant="body1">No enrollment data available</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <Chart ref={chartRef} dataSource={data} rotated={true} height="100%">
                    <Title text={exportMetadata.title} subtitle={exportMetadata.subtitle}>
                        <Font size={18} weight={600} />
                    </Title>
                    <Series
                        valueField="enrollmentCount"
                        argumentField="schoolName"
                        type="bar"
                        color="#3182bd"
                    />
                    <ArgumentAxis>
                        <Label wordWrap="none" overlappingBehavior="rotate" />
                    </ArgumentAxis>
                    <ValueAxis>
                        <Title text="Enrollment Count" />
                    </ValueAxis>
                    <Legend visible={false} />
                    <Tooltip
                        enabled={true}
                        customizeTooltip={(pointInfo: any) => ({
                            text: `${pointInfo.argumentText}: ${pointInfo.valueText} students`,
                        })}
                    />
                    
                    <Export enabled={true} />
                </Chart>
            </Box>
        </Box>
    );
};